import { useControllableValue } from 'ahooks';
import { Slider as AntSlider, Space } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC } from 'react';
import styled from 'styled-components';
import { InputNumber as InputNumberAtom } from 'components/atoms/input';
import { ClassNameProps } from 'components/ozone-component';

interface ProgressBarProps extends ClassNameProps {
  step?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
  value?: number;
  disabled?: boolean;
  size?: SizeType;
  ref?: React.RefObject<HTMLDivElement>;
  inputRef?: React.Ref<HTMLInputElement>;
  onAfterChange?: (value: number) => void;
  onInputPressEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onInputBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const InputNumber = styled(InputNumberAtom)`
  width: 72px;
  min-width: 72px;
  max-width: 72px;
  background: var(--white-100);
  border: none;
  padding-left: 4px;
`;

const Slider = styled(AntSlider)`
  min-width: 80px;
  margin: var(--spacing-04) var(--spacing-05);
  & .ant-slider-rail {
    height: 8px;
    border-radius: 0;
  }
  & .ant-slider-track {
    height: 8px;
    background-color: var(--green-50);
    border-radius: 0;
  }
  .ant-slider-step {
    height: 8px;
  }
  .ant-slider-handle {
    margin-top: -4px;
    width: 16px;
    height: 16px;
    border-color: var(--green-50);
    &:focus {
      box-shadow: 0 0 0 5px rgba(25, 128, 56, 0.12);
    }
  }
  &:hover {
    .ant-slider-track {
      background-color: var(--green-50);
    }

    .ant-slider-handle:not(.ant-tooltip-open) {
      border-color: var(--green-60);
    }
  }
`;

export const ProgressBar: FC<ProgressBarProps> = (props) => {
  const {
    className,
    ref,
    inputRef,
    size,
    step = 10,
    min = 0,
    max = 100,
    onAfterChange,
    onInputBlur,
    onInputPressEnter,
    disabled,
  } = props;
  const [state, setState] = useControllableValue<number | undefined>(props, {
    defaultValue: 0,
  });

  return (
    <Space size={0} className={className}>
      <Slider
        ref={ref}
        step={step}
        min={min}
        max={max}
        onChange={(value: number) => setState(value)}
        onAfterChange={onAfterChange}
        value={state}
        disabled={disabled}
      />
      <InputNumber
        ref={inputRef}
        size={size}
        step={step}
        min={min}
        max={max}
        value={state}
        formatter={(value) => `${value}%`}
        parser={(value) => value?.replace('%', '') || ''}
        onChange={(value) => setState(value ? Number(value) : 0)}
        onPressEnter={onInputPressEnter}
        onBlur={onInputBlur}
        disabled={disabled}
      />
    </Space>
  );
};
